import { searchContext } from '../../../../context/Context';
import { useMappingDrillDown } from '../../../../context/MappingDrillDownContext';
import { DATASETS } from '../../../Constants';
import updateParams from '../../../../utils/updateParams';
import { getHeaderProps } from '../config/helpers';
import { renderMappingButton } from '../renders/buttonRenderers';
import RestrictedContent from '../../components/RestrictedContent';
import RestrictedHeader from '../../components/RestrictedHeader';

function resolveGeneId(data: any): string | number | undefined {
  return (
    data?.geneID ??
    data?.ncbiID ??
    data?.geneNcbiID ??
    data?.gene_ncbi_id ??
    (Array.isArray(data?.geneNcbiIDs) ? data.geneNcbiIDs[0] : data?.geneNcbiIDs)
  );
}

export const getMetricsColumns = context => {
  const { tab, isGDA, isVDA, isAuthenticated, isAcademic } = context;
  const headerProps = getHeaderProps();

  const isChemicalRestricted = isAuthenticated && isAcademic;

  return [
    {
      name: 'numCTs',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : tab,
      sortable: true,
      header: <span>N CTs</span>,
      headerProps,
      columnSize: 10,
      render: ({ data }) => {
        const { tab } = searchContext();
        const nRaw = Number(data?.numCTs);
        const n = Number.isFinite(nRaw) ? nRaw : 0;

        if (tab === 'GDA') {
          if (n === 0) return 0;
          const config = {
            displayValue: data.numCTs,
            mappingItems: [
              {
                filterParam: 'disease',
                filterValue: data.diseaseUMLSCUI,
                name: data.diseaseName,
                id: data.diseaseUMLSCUI,
              },
              {
                filterParam: 'gene_ncbi_id',
                filterValue: data.geneNcbiID,
                name: data.geneSymbol,
                id: data.geneDescription,
              },
              {
                filterParam: 'reference_type',
                filterValue: 'NCTID',
              },
              {
                filterParam: 'source',
                filterValue: 'CLINICALTRIALS',
              },
            ],
            columVisibilityModelKey: 'GENES_GDA',
            tabSegment: 'gda',
            name: 'numCTs',
          };

          return renderMappingButton(data, config);
        }

        if (tab === 'DISEASES') {
          const newParams = updateParams({
            view: DATASETS.DISEASES,
            tab: 'GDA',
            searchItems: [
              {
                id: data.diseaseUMLSCUI,
                name: data.diseaseName,
                filterParam: 'disease',
                filterValue: data.diseaseUMLSCUI,
              },
            ],
            source: 'CLINICALTRIALS',
          });

          const nRaw = Number(data?.numCTs);
          const n = Number.isFinite(nRaw) ? nRaw : 0;
          if (n === 0) return 0;

          return (
            <a className="tab-links" href={`?${newParams}`}>
              {data.numCTs}
            </a>
          );
        }

        if (tab === 'GENES') {
          const nRaw = Number(data?.numCTs);
          const n = Number.isFinite(nRaw) ? nRaw : 0;
          if (n === 0) return 0;

          const geneId = resolveGeneId(data);
          const newParams = updateParams({
            view: DATASETS.GENES,
            tab: 'GDA',
            searchItems: [{ id: String(geneId) }],
            source: 'CLINICALTRIALS',
          });

          return (
            <a className="tab-links" href={`?${newParams}`}>
              {data.numCTs}
            </a>
          );
        }

        if (tab === 'CHEMICALS') {
          const newParams = updateParams({
            view: DATASETS.CHEMICALS,
            tab: 'GDA',
            searchItems: [{ id: data.chemID }],
            source: 'CLINICALTRIALS',
          });

          return (
            <a className="tab-links" href={`?${newParams}`}>
              {data.numPublications_ALL_NCTs}
            </a>
          );
        }

        return data.numPublications_ALL_NCTs;
      },
    },

    {
      name: 'citation_count',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="">Citation Count</span>,
      headerProps,
      minWidth: 160,
      defaultFlex: 1,
      render: ({ data }) => {
        const citationCount = data?.citation_count;

        if (citationCount === null || citationCount === undefined) {
          return '-';
        }

        // Para la release: no renderizamos link externo para `citation_count`.
        return citationCount;
      },
    },

    {
      name: 'numChemicals',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : tab,
      sortable: !isChemicalRestricted,
      minWidth: 115,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="">N Chemicals</span>
        </RestrictedHeader>
      ),
      headerProps,
      render: ({ data }) => {
        const { tab } = searchContext();
        let mappingItems = [];
        const columVisibilityModelKey = 'numChemicalsDISEASES';
        const tabSegment = 'chem';

        if (tab === 'DISEASES') {
          mappingItems = [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.name,
              id: data.diseaseUMLSCUI,
            },
          ];
        } else if (tab === 'GENES') {
          mappingItems = [
            {
              filterParam: 'geneSymbol',
              filterValue: data.symbol,
              name: data.symbol,
              id: data.geneID,
            },
          ];
        } else if (tab === 'VARIANTS') {
          mappingItems = [
            {
              filterParam: 'variantStrID',
              filterValue: data.strID,
              name: data.geneSymbol,
              id: data.strID,
            },
          ];
        }

        if (!data.numChemicals || data.numChemicals === 0) {
          return <RestrictedContent isRestricted={isChemicalRestricted}>0</RestrictedContent>;
        }

        const config = {
          displayValue: data.numChemicals,
          mappingItems,
          columVisibilityModelKey,
          tabSegment,
          name: 'numChemicals',
          viewSegment: '/all',
        };

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {renderMappingButton(data, config)}
          </RestrictedContent>
        );
      },
    },

    {
      name: 'sourceScore',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="">My Score</span>,
      headerProps,
      minWidth: 160,
      defaultFlex: 1,
      render: ({ data }) => {
        if (data.sourceScore !== null && data.sourceScore !== undefined) {
          return data.sourceScore.toFixed(2);
        }
        return null;
      },
    },

    {
      name: 'numberPmidsWithChemsIncludedInEvidence',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : tab,
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Number of Pmids With Chems Included in the Evidences">N PMIDs Chemicals</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 180,
      render: ({ data }) => {
        const { tab } = searchContext();
        const { effectiveTab } = useMappingDrillDown();
        const resolvedTab = effectiveTab || tab;

        let mappingItems = [];
        let columVisibilityModelKey = '';
        let tabSegment = '';

        if (resolvedTab === 'GDA') {
          columVisibilityModelKey = 'numberPmidsWithChemsIncludedInEvidenceGDA';
          tabSegment = 'gda';
          mappingItems = [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'gene_ncbi_id',
              filterValue: data.geneNcbiID,
              name: data.geneSymbol,
              id: data.geneDescription,
            },
            {
              filterParam: 'chem_ids_filter',
              filterValue: 'ALL',
            },
          ];
        } else if (resolvedTab === 'VDA') {
          columVisibilityModelKey = 'numberPmidsWithChemsIncludedInEvidenceVDA';
          tabSegment = 'vda';
          mappingItems = [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'variant',
              filterValue: data.variantStrID,
              name: data.variantStrID,
              id: data.geneSymbol,
            },
            {
              filterParam: 'chem_ids_filter',
              filterValue: 'ALL',
            },
          ];
        } else if (resolvedTab === 'GENES') {
          columVisibilityModelKey = 'numberPmidsWithChemsIncludedInEvidenceGDA';
          tabSegment = 'gda';
          mappingItems = [
            {
              filterParam: 'gene_symbol',
              filterValue: data.symbol,
              name: data.symbol,
              id: data.geneID,
            },
            {
              filterParam: 'chem_ids_filter',
              filterValue: 'ALL',
            },
          ];
        } else if (resolvedTab === 'VARIANTS') {
          columVisibilityModelKey = 'numberPmidsWithChemsIncludedInEvidenceVDA';
          tabSegment = 'vda';
          mappingItems = [
            {
              filterParam: 'variant',
              filterValue: data.strID,
              name: data.geneSymbol,
              id: data.strID,
            },
            {
              filterParam: 'chem_ids_filter',
              filterValue: 'ALL',
            },
          ];
        }

        if (
          !data.numberPmidsWithChemsIncludedInEvidence ||
          data.numberPmidsWithChemsIncludedInEvidence === 0
        ) {
          return <RestrictedContent isRestricted={isChemicalRestricted}>0</RestrictedContent>;
        }

        const config = {
          displayValue: data.numberPmidsWithChemsIncludedInEvidence,
          mappingItems,
          columVisibilityModelKey,
          tabSegment,
          name: 'numberPmidsWithChemsIncludedInEvidence',
          viewSegment: '/all',
        };

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {renderMappingButton(data, config)}
          </RestrictedContent>
        );
      },
    },

    {
      name: 'numberNCTSWithChemsIncludedInEvidence',
      category: 'GDA',
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Number of Clinical Trial With Chems Included in the Evidences">N CTs Chemicals</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 180,
      render: ({ data }) => {
        if (!data.numberNCTSWithChemsIncludedInEvidence || data.numberNCTSWithChemsIncludedInEvidence === 0) {
          return <RestrictedContent isRestricted={isChemicalRestricted}>0</RestrictedContent>;
        }

        const config = {
          displayValue: data.numberNCTSWithChemsIncludedInEvidence,
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'gene_ncbi_id',
              filterValue: data.geneNcbiID,
              name: data.geneSymbol,
              id: data.geneDescription,
            },
            {
              filterParam: 'chem_ids_filter',
              filterValue: 'ALL',
            },
          ],
          columVisibilityModelKey: 'numberPmidsWithChemsIncludedInEvidenceGDA',
          tabSegment: 'gda',
          name: 'numberNCTSWithChemsIncludedInEvidence',
          viewSegment: '/all', // N Chemicals debe usar /all
        };

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {renderMappingButton(data, config)}
          </RestrictedContent>
        );
      },
    },

    {
      name: 'chemNumPublicationsGDA',
      category: 'GDA',
      sortable: true,
      header: (
        <span title="Number of Publications mentioning this disease-gene association containing the chemical">
          N PMIDs this GDA
        </span>
      ),
      headerProps,
      minWidth: 195,
      render: ({ data }) => {
        const config = {
          displayValue: data.chemNumPublicationsGDA,
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'gene_ncbi_id',
              filterValue: data.geneNcbiID,
              name: data.geneSymbol,
              id: data.geneDescription,
            },
            {
              filterParam: 'chem_ids_filter',
              filterValue: data.chemID,
              name: data.chemPrefName,
              id: data.chemID,
            },
          ],
          columVisibilityModelKey: 'GDA_ALL',
          tabSegment: 'gda',
          name: 'chemNumPublicationsGDA',
          viewSegment: '/all', // N Chemicals debe usar /all
        };

        return renderMappingButton(data, config);
      },
    },

    {
      name: 'chemNumPublicationsVDA',
      category: 'VDA',
      sortable: true,
      header: (
        <span title="Number of Publications mentioning this disease-variant association containing the chemical">
          N PMIDs this VDA
        </span>
      ),
      headerProps,
      minWidth: 195,
      render: ({ data }) => {
        const config = {
          displayValue: data.chemNumPublicationsVDA,
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'variant',
              filterValue: data.variantStrID,
              name: data.variantStrID,
              id: data.geneSymbol,
            },
            {
              filterParam: 'chem_ids_filter',
              filterValue: data.chemID,
              name: data.chemPrefName,
              id: data.chemID,
            },
          ],
          columVisibilityModelKey: 'VDA_ALL',
          tabSegment: 'vda',
          name: 'chemNumPublicationsVDA',
          viewSegment: '/all', // N Chemicals debe usar /all
        };

        return renderMappingButton(data, config);
      },
    },
  ];
};
